import useAuthHasUserClass from '@apps/www/src/www/hooks/useAuthHasUserClass';
import usePersistentSetting, {
	PersistentSettingKeys,
} from '@apps/www/src/www/hooks/usePersistentSetting';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import useIsomorphicLayoutEffect from '@pkgs/shared-client/hooks/useIsomorphicLayoutEffect';
import SVFeedIconSVG from '@pkgs/shared-client/img/icon-feed-grid-inlined.svg';
import SVGridIconSVG from '@pkgs/shared-client/img/icon-grid-inlined.svg';
import GridLayoutMode from '@pkgs/shared/enums/GridLayoutMode';
import UserClass from '@pkgs/shared/enums/UserClass';
import cn from '@pkgs/shared-client/helpers/cn';
import { useCallback, useEffect, useRef, useState } from 'react';

type LayoutOption = [
	(typeof GridLayoutMode)[keyof typeof GridLayoutMode],
	string,
	React.ComponentType<any>,
];

const LAYOUT_LABEL_OPTIONS: LayoutOption[] = [
	[GridLayoutMode.GRID, 'Grid', SVGridIconSVG],
	[GridLayoutMode.CARD, 'Feed', SVFeedIconSVG],
];

const SVFeedLayoutToggleContainer = ({ isShortMode = false }: { isShortMode?: boolean }) => {
	const [gridLayoutMode, setGridLayoutMode] = usePersistentSetting(
		PersistentSettingKeys.GRID_LAYOUT_MODE,
	);
	const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
	const pillRef = useRef<HTMLDivElement>(null);
	const [isInitialRender, setIsInitialRender] = useState(true);
	const [forceUpdate, setForceUpdate] = useState(0);

	const generatePillPosition = useCallback(() => {
		const selectedIndex = LAYOUT_LABEL_OPTIONS.findIndex(([type]) => type === gridLayoutMode);

		const selectedElement = optionRefs.current[selectedIndex];

		if (selectedElement && pillRef.current) {
			pillRef.current.style.width = `${selectedElement.offsetWidth}px`;
			pillRef.current.style.height = `${selectedElement.offsetHeight}px`;
			pillRef.current.style.transform = `translateX(${selectedElement.offsetLeft - 4}px)`;

			if (isInitialRender) {
				setTimeout(() => setIsInitialRender(false), 0);
			}
		}
	}, [gridLayoutMode, isInitialRender]);

	useIsomorphicLayoutEffect(() => {
		generatePillPosition();
	}, [
		gridLayoutMode,
		isInitialRender,
		isShortMode,
		setGridLayoutMode,
		generatePillPosition,
		forceUpdate,
	]);

	useEffect(() => {
		const handleResize = () => {
			setForceUpdate((prev) => prev + 1);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setForceUpdate((prev) => prev + 1);
		}, 50);

		return () => clearTimeout(timeoutId);
	}, [isShortMode]);

	const isModerator = useAuthHasUserClass(UserClass.MODERATOR);

	const handleLayoutChange = useEventCallback(
		(layoutTypeOption: (typeof GridLayoutMode)[keyof typeof GridLayoutMode]) => {
			setGridLayoutMode(layoutTypeOption);
		},
	);

	if (!isModerator) {
		return null;
	}

	return (
		<div
			className={cn(
				'bg-background z-10',
				isShortMode
					? 'flex'
					: '-lg:hidden fixed bottom-0 left-0 min-w-[calc(var(--page-margin)+240px+12px)] p-[var(--page-margin)]',
			)}
		>
			<div className="relative flex w-fit items-center rounded-lg bg-gray-900 p-1">
				<div
					ref={pillRef}
					className={cn(
						'bg-primary absolute rounded',
						!isInitialRender && 'duration-over transition-all ease-in-out',
					)}
					style={{ top: '4px' }}
				/>
				{LAYOUT_LABEL_OPTIONS.map(([layoutTypeOption, label, IconComponent], index) => (
					<div
						key={layoutTypeOption}
						ref={(el) => (optionRefs.current[index] = el)}
						className={cn(
							'type-small z-10 flex cursor-pointer items-center gap-1.5 rounded px-[15px] py-1 font-medium',
							'transition-colors duration-300 ease-in-out',
							gridLayoutMode === layoutTypeOption
								? 'text-gray-600 hover:text-gray-600'
								: 'text-gray-400 hover:text-gray-300',
							isShortMode && 'px-3 py-3',
						)}
						onClick={() => handleLayoutChange(layoutTypeOption)}
					>
						<IconComponent className={cn(isShortMode && 'h-4 w-4')} />
						{isShortMode ? null : label}
					</div>
				))}
			</div>
		</div>
	);
};

export default SVFeedLayoutToggleContainer;
